import React, { useState, useEffect } from 'react';
/* eslint-disable react-hooks/exhaustive-deps */
import { Container, Row, Col, Card, Form, Button, Table, Modal, Alert, Spinner, Badge } from 'react-bootstrap';
import { FiPlus, FiEdit2, FiTrash2, FiEye, FiFilter, FiSave, FiFolder } from 'react-icons/fi';
import { projectAPI, departmentAPI } from '../utils/api';
import './ProjectsPage.css';

const ProjectsPage = () => {
  const getApiErrorMessage = (err, fallback) => {
    if (Array.isArray(err?.errors) && err.errors.length > 0) {
      return err.errors.map((item) => item?.message).filter(Boolean).join(', ');
    }
    return err?.message || fallback;
  };

  // States
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [departments, setDepartments] = useState([]);

  const [selectedDept, setSelectedDept] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Modal States
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);

  // Form States
  const [formData, setFormData] = useState({
    name: '',
    projectCode: '',
    description: '',
    departmentId: '',
    status: 'active'
  });

  const [selectedProject, setSelectedProject] = useState(null);
  const [projectToDelete, setProjectToDelete] = useState(null);

  /**
   * Fetch initial data
   */
  useEffect(() => {
    fetchInitialData();
  }, []);

  /**
   * Fetch projects and departments
   */
  const fetchInitialData = async () => {
    try {
      setLoading(true);
      setError('');

      // Fetch departments
      const deptsResponse = await departmentAPI.getAllDepartments();
      if (deptsResponse.success && deptsResponse.data) {
        setDepartments(deptsResponse.data);
        if (deptsResponse.data.length > 0) {
          setSelectedDept(deptsResponse.data[0].id);
        }
      }

      // Fetch projects
      const projResponse = await projectAPI.getAllProjects();
      if (projResponse.success && projResponse.data) {
        setProjects(projResponse.data);
        applyFilters(projResponse.data, deptsResponse.data[0]?.id || null, '', 'all');
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Apply all filters
   */
  const applyFilters = (allProjects, deptId, search, status) => {
    let filtered = allProjects;

    // Filter by department
    if (deptId) {
      filtered = filtered.filter(p => p.departmentId === deptId);
    }

    // Filter by search term
    if (search) {
      filtered = filtered.filter(p =>
        p.name.toLowerCase().includes(search.toLowerCase()) ||
        p.projectCode.toLowerCase().includes(search.toLowerCase()) ||
        (p.description && p.description.toLowerCase().includes(search.toLowerCase()))
      );
    }

    // Filter by status
    if (status && status !== 'all') {
      filtered = filtered.filter(p => p.status === status);
    }

    setFilteredProjects(filtered);
  };

  /**
   * Handle department change
   */
  const handleDeptChange = (deptId) => {
    setSelectedDept(deptId);
    applyFilters(projects, deptId, searchTerm, statusFilter);
  };

  /**
   * Handle search
   */
  const handleSearch = (term) => {
    setSearchTerm(term);
    applyFilters(projects, selectedDept, term, statusFilter);
  };

  /**
   * Handle status filter
   */
  const handleStatusFilter = (status) => {
    setStatusFilter(status);
    applyFilters(projects, selectedDept, searchTerm, status);
  };

  /**
   * Handle form input change
   */
  const handleFormChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  /**
   * Reset form
   */
  const resetForm = () => {
    setFormData({
      name: '',
      projectCode: '',
      description: '',
      departmentId: selectedDept || '',
      status: 'active'
    });
  };

  /**
   * Open create modal
   */
  const handleOpenCreateModal = () => {
    resetForm();
    setShowCreateModal(true);
  };

  /**
   * Open edit modal
   */
  const handleOpenEditModal = (project) => {
    setSelectedProject(project);
    setFormData({
      name: project.name,
      projectCode: project.projectCode,
      description: project.description,
      departmentId: project.departmentId,
      status: project.status
    });
    setShowEditModal(true);
  };

  /**
   * Open view modal
   */
  const handleOpenViewModal = (project) => {
    setSelectedProject(project);
    setShowViewModal(true);
  };

  /**
   * Open delete confirmation modal
   */
  const handleOpenDeleteModal = (project) => {
    setProjectToDelete(project);
    setShowDeleteModal(true);
  };

  /**
   * Validate form
   */
  const validateForm = () => {
    if (!formData.name.trim()) {
      setError('Project name is required');
      return false;
    }

    if (!formData.projectCode.trim()) {
      setError('Project code is required');
      return false;
    }

    if (!formData.departmentId) {
      setError('Department is required');
      return false;
    }

    return true;
  };

  /**
   * Handle create project
   */
  const handleCreateProject = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      setSuccess('');

      const response = await projectAPI.createProject(formData);

      if (response.success && response.data) {
        setSuccess('Project created successfully!');
        setProjects([...projects, response.data]);
        applyFilters([...projects, response.data], selectedDept, searchTerm, statusFilter);
        setShowCreateModal(false);
        resetForm();
      }
    } catch (err) {
      console.error('Create error:', err);
      setError(getApiErrorMessage(err, 'Failed to create project'));
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Handle update project
   */
  const handleUpdateProject = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      setSuccess('');

      const response = await projectAPI.updateProject(selectedProject.id, formData);

      if (response.success && response.data) {
        setSuccess('Project updated successfully!');
        setProjects(projects.map(p => p.id === selectedProject.id ? response.data : p));
        applyFilters(
          projects.map(p => p.id === selectedProject.id ? response.data : p),
          selectedDept,
          searchTerm,
          statusFilter
        );
        setShowEditModal(false);
        resetForm();
      }
    } catch (err) {
      console.error('Update error:', err);
      setError(getApiErrorMessage(err, 'Failed to update project'));
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Handle delete project
   */
  const handleDeleteProject = async () => {
    try {
      setSubmitting(true);
      setError('');
      setSuccess('');

      await projectAPI.deleteProject(projectToDelete.id);

      setSuccess('Project deleted successfully!');
      setProjects(projects.filter(p => p.id !== projectToDelete.id));
      applyFilters(
        projects.filter(p => p.id !== projectToDelete.id),
        selectedDept,
        searchTerm,
        statusFilter
      );
      setShowDeleteModal(false);
      setProjectToDelete(null);
    } catch (err) {
      console.error('Delete error:', err);
      setError(getApiErrorMessage(err, 'Failed to delete project'));
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Get department name
   */
  const getDepartmentName = (deptId) => {
    return departments.find(d => d.id === deptId)?.name || 'N/A';
  };

  if (loading) {
    return (
      <Container fluid className="py-5 text-center">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Loading projects...</p>
      </Container>
    );
  }

  return (
    <Container fluid className="projects-page py-4">
      {/* Error Alert */}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Success Alert */}
      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Header */}
      <div className="projects-header mb-4">
        <div>
          <h1 className="mb-2">Projects</h1>
          <p className="text-muted">Create, manage, and track your business projects</p>
        </div>
        <Button
          variant="primary"
          size="lg"
          onClick={handleOpenCreateModal}
          className="create-btn"
        >
          <FiPlus className="me-2" />
          Create Project
        </Button>
      </div>

      {/* Filters Section */}
      <Card className="filters-card shadow-sm mb-4">
        <Card.Header className="bg-white border-bottom">
          <h5 className="mb-0">
            <FiFilter className="me-2" />
            Filters
          </h5>
        </Card.Header>

        <Card.Body>
          <Row className="g-3">
            {/* Department Filter */}
            <Col md={3}>
              <Form.Group>
                <Form.Label className="filter-label">Department</Form.Label>
                <Form.Select
                  value={selectedDept || ''}
                  onChange={(e) => handleDeptChange(e.target.value ? parseInt(e.target.value) : null)}
                  className="filter-select"
                >
                  <option value="">All Departments</option>
                  {departments.map(dept => (
                    <option key={dept.id} value={dept.id}>
                      {dept.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>

            {/* Status Filter */}
            <Col md={3}>
              <Form.Group>
                <Form.Label className="filter-label">Status</Form.Label>
                <Form.Select
                  value={statusFilter}
                  onChange={(e) => handleStatusFilter(e.target.value)}
                  className="filter-select"
                >
                  <option value="all">All Statuses</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="completed">Completed</option>
                </Form.Select>
              </Form.Group>
            </Col>

            {/* Search */}
            <Col md={6}>
              <Form.Group>
                <Form.Label className="filter-label">Search</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Search projects by name or code..."
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="filter-input"
                />
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Projects Table */}
      <Card className="projects-card shadow-sm">
        <Card.Header className="bg-white border-bottom d-flex justify-content-between align-items-center">
          <h5 className="mb-0">
            Projects
            <Badge bg="primary" className="ms-2">
              {filteredProjects.length}
            </Badge>
          </h5>
        </Card.Header>

        <Card.Body className="p-0">
          {filteredProjects.length === 0 ? (
            <div className="no-projects text-center py-5">
              <div className="no-projects-icon">📁</div>
              <p className="text-muted mt-3 mb-2">
                {projects.length === 0 ? 'No projects created yet' : 'No projects match your filters'}
              </p>
              <Button
                variant="primary"
                size="sm"
                onClick={handleOpenCreateModal}
              >
                <FiPlus className="me-1" />
                Create First Project
              </Button>
            </div>
          ) : (
            <div className="table-responsive">
              <Table hover className="projects-table">
                <thead>
                  <tr>
                    <th>Project Name</th>
                    <th>Code</th>
                    <th>Department</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProjects.map(project => (
                    <tr key={project.id} className="project-row">
                      <td>
                        <div className="project-name-cell">
                          <FiFolder className="project-icon" />
                          <div>
                            <div className="project-name">{project.name}</div>
                            <div className="project-description">{project.description}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <code className="project-code">{project.projectCode}</code>
                      </td>
                      <td>
                        <Badge bg="light" text="dark" className="dept-badge">
                          {getDepartmentName(project.departmentId)}
                        </Badge>
                      </td>
                      <td>
                        <StatusBadge status={project.status} />
                      </td>
                      <td>
                        <small>{new Date(project.createdAt).toLocaleDateString()}</small>
                      </td>
                      <td>
                        <div className="project-actions">
                          <Button
                            variant="outline-info"
                            size="sm"
                            onClick={() => handleOpenViewModal(project)}
                            title="View"
                            className="action-btn"
                          >
                            <FiEye size={16} />
                          </Button>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleOpenEditModal(project)}
                            title="Edit"
                            className="action-btn"
                          >
                            <FiEdit2 size={16} />
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleOpenDeleteModal(project)}
                            title="Delete"
                            className="action-btn"
                          >
                            <FiTrash2 size={16} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Create Modal */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} centered size="lg">
        <Modal.Header closeButton className="border-bottom">
          <Modal.Title>
            <FiPlus className="me-2" />
            Create New Project
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <ProjectForm
            formData={formData}
            handleFormChange={handleFormChange}
            departments={departments}
            submitting={submitting}
            onSubmit={handleCreateProject}
          />
        </Modal.Body>

        <Modal.Footer className="border-top">
          <Button
            variant="secondary"
            onClick={() => {
              setShowCreateModal(false);
              resetForm();
            }}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleCreateProject}
            disabled={submitting}
          >
            {submitting ? (
              <>
                <Spinner as="span" animation="border" size="sm" className="me-2" />
                Creating...
              </>
            ) : (
              <>
                <FiPlus className="me-2" />
                Create Project
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered size="lg">
        <Modal.Header closeButton className="border-bottom">
          <Modal.Title>
            <FiEdit2 className="me-2" />
            Edit Project
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <ProjectForm
            formData={formData}
            handleFormChange={handleFormChange}
            departments={departments}
            submitting={submitting}
            onSubmit={handleUpdateProject}
          />
        </Modal.Body>

        <Modal.Footer className="border-top">
          <Button
            variant="secondary"
            onClick={() => {
              setShowEditModal(false);
              resetForm();
            }}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleUpdateProject}
            disabled={submitting}
          >
            {submitting ? (
              <>
                <Spinner as="span" animation="border" size="sm" className="me-2" />
                Updating...
              </>
            ) : (
              <>
                <FiSave className="me-2" />
                Update Project
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* View Modal */}
      <Modal show={showViewModal} onHide={() => setShowViewModal(false)} centered size="lg">
        <Modal.Header closeButton className="border-bottom">
          <Modal.Title>
            <FiEye className="me-2" />
            Project Details
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {selectedProject && (
            <div className="project-details">
              <div className="detail-group">
                <label>Project Name</label>
                <p>{selectedProject.name}</p>
              </div>

              <div className="detail-group">
                <label>Project Code</label>
                <p><code>{selectedProject.projectCode}</code></p>
              </div>

              <div className="detail-group">
                <label>Department</label>
                <p>{getDepartmentName(selectedProject.departmentId)}</p>
              </div>

              <div className="detail-group">
                <label>Status</label>
                <p><StatusBadge status={selectedProject.status} /></p>
              </div>

              <div className="detail-group">
                <label>Description</label>
                <p>{selectedProject.description || 'No description'}</p>
              </div>

              <div className="detail-group">
                <label>Created Date</label>
                <p>{new Date(selectedProject.createdAt).toLocaleString()}</p>
              </div>

              {selectedProject.updatedAt && (
                <div className="detail-group">
                  <label>Last Updated</label>
                  <p>{new Date(selectedProject.updatedAt).toLocaleString()}</p>
                </div>
              )}
            </div>
          )}
        </Modal.Body>

        <Modal.Footer className="border-top">
          <Button variant="secondary" onClick={() => setShowViewModal(false)}>
            Close
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              setShowViewModal(false);
              handleOpenEditModal(selectedProject);
            }}
          >
            <FiEdit2 className="me-2" />
            Edit
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton className="border-bottom">
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {projectToDelete && (
            <div className="delete-confirmation">
              <div className="warning-icon">⚠️</div>
              <p className="mt-3">
                Are you sure you want to delete <strong>{projectToDelete.name}</strong>?
              </p>
              <p className="text-muted small">
                This action cannot be undone. All associated files and data will be removed.
              </p>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer className="border-top">
          <Button
            variant="secondary"
            onClick={() => {
              setShowDeleteModal(false);
              setProjectToDelete(null);
            }}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDeleteProject}
            disabled={submitting}
          >
            {submitting ? (
              <>
                <Spinner as="span" animation="border" size="sm" className="me-2" />
                Deleting...
              </>
            ) : (
              <>
                <FiTrash2 className="me-2" />
                Delete Project
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

/**
 * ProjectForm Component - Reusable form for Create/Edit modals
 */
const ProjectForm = ({ formData, handleFormChange, departments, submitting, onSubmit }) => {
  return (
    <Form onSubmit={onSubmit}>
      {/* Project Name */}
      <Form.Group className="mb-3">
        <Form.Label>Project Name <span className="text-danger">*</span></Form.Label>
        <Form.Control
          type="text"
          placeholder="Enter project name"
          value={formData.name}
          onChange={(e) => handleFormChange('name', e.target.value)}
          disabled={submitting}
          className="form-input"
        />
      </Form.Group>

      {/* Project Code */}
      <Form.Group className="mb-3">
        <Form.Label>Project Code <span className="text-danger">*</span></Form.Label>
        <Form.Control
          type="text"
          placeholder="e.g., PROJ001"
          value={formData.projectCode}
          onChange={(e) => handleFormChange('projectCode', e.target.value.toUpperCase())}
          disabled={submitting}
          className="form-input"
        />
      </Form.Group>

      {/* Department */}
      <Form.Group className="mb-3">
        <Form.Label>Department <span className="text-danger">*</span></Form.Label>
        <Form.Select
          value={formData.departmentId}
          onChange={(e) => handleFormChange('departmentId', e.target.value ? parseInt(e.target.value) : '')}
          disabled={submitting}
          className="form-input"
        >
          <option value="">Select Department</option>
          {departments.map(dept => (
            <option key={dept.id} value={dept.id}>
              {dept.name}
            </option>
          ))}
        </Form.Select>
      </Form.Group>

      {/* Status */}
      <Form.Group className="mb-3">
        <Form.Label>Status</Form.Label>
        <Form.Select
          value={formData.status}
          onChange={(e) => handleFormChange('status', e.target.value)}
          disabled={submitting}
          className="form-input"
        >
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="completed">Completed</option>
        </Form.Select>
      </Form.Group>

      {/* Description */}
      <Form.Group className="mb-3">
        <Form.Label>Description</Form.Label>
        <Form.Control
          as="textarea"
          rows={4}
          placeholder="Enter project description..."
          value={formData.description}
          onChange={(e) => handleFormChange('description', e.target.value)}
          disabled={submitting}
          className="form-input"
        />
      </Form.Group>
    </Form>
  );
};

/**
 * Status Badge Component
 */
const StatusBadge = ({ status }) => {
  const statusConfig = {
    active: { bg: 'success', text: 'Active' },
    inactive: { bg: 'secondary', text: 'Inactive' },
    completed: { bg: 'info', text: 'Completed' }
  };

  const config = statusConfig[status] || { bg: 'secondary', text: 'Unknown' };

  return (
    <Badge bg={config.bg}>
      {config.text}
    </Badge>
  );
};

export default ProjectsPage;
